/**
 * TutorialReplayEngine - Micro-Slide Engine for v2.0 tutorial format
 *
 * Expands compact codeSteps into detailed micro-slides that each represent
 * one user action (click select, choose block, open menu, toggle hardware, etc.).
 * This matches the old 28-step granular behaviour where every interaction was
 * its own visible tutorial step.
 *
 * Supported block types: action, wait, decision, loop, end/repeat, sensor, variable, if
 * Supported actions: PLACE_BLOCK, INSERT_BLOCK, DELETE_BLOCK, OPEN_MENU, CLOSE_MENU, SET_VALUE, TOGGLE_HARDWARE, SET_STATE
 */

// ──────────────────────────────────────────────
// Internal constants
// ──────────────────────────────────────────────

const BLOCK_TYPE_MAP = {
    action: 'hardware',
    wait: 'wait',
    end: 'end',
    decision: 'condition',
    condition: 'condition',
    loop: 'loop',
    repeat: 'repeat',
    sensor: 'sensor',
    variable: 'variable',
    if: 'if',
    stop: 'end',
}

const TYPES_WITH_SUBPROGRAM = ['condition', 'sensor', 'if', 'loop', 'variable']

const BLOCK_LABELS = {
    action: 'Action',
    wait: 'Wait',
    end: 'Stop',
    stop: 'Stop',
    decision: 'Decision',
    condition: 'Condition',
    loop: 'Loop',
    repeat: 'Repeat',
    sensor: 'Sensor',
    variable: 'Variable',
    if: 'If',

    end_loop: 'End Loop',
    end_condition: 'End Decision',
    end_sensor: 'End Sensor',
    end_variable: 'End Variable',
    end_if: 'End If',
}

const NEW_BLOCK_POSITIONS_DIFF = {
    action: { row: 1, col: 1 },
    wait: { row: 1, col: 0 },
    condition: { row: -1, col: 1 },
    loop: { row: -1, col: 0 },
    end: { row: 0, col: 1 },

    // all the end_* go in same position as regular end
    end_loop: { row: 0, col: 1 },
    end_condition: { row: 0, col: 1 },
    end_sensor: { row: 0, col: 1 },
    end_variable: { row: 0, col: 1 },
    end_if: { row: 0, col: 1 },
}

let nextBlockId = 10

const resetBlockIdCounter = () => {
    nextBlockId = 10
}

const generateBlockId = () => {
    nextBlockId++
    return String(nextBlockId)
}

export const getNewBlockPosition = (boardTarget, blockType) => {
    const parts = boardTarget.split('-')
    const row = parseInt(parts[2], 10)
    const col = parseInt(parts[3], 10)

    const diff = NEW_BLOCK_POSITIONS_DIFF[blockType]

    if (!diff) return boardTarget

    const newRow = row + diff.row
    const newCol = col + diff.col

    console.log(
        'Calculating new block position for type:',
        blockType,
        `hex-block-${newRow}-${newCol}`
    )

    return `hex-block-${newRow}-${newCol}`
}

const getExistingBlockOptions = (boardTarget, action) => {
    const parts = boardTarget.split('-')
    const row = parseInt(parts[2], 10)
    const col = parseInt(parts[3], 10)

    let newRow = row
    let newCol = col

    if (action === 'INSERT_BLOCK') {
        newRow = row - 1
        newCol = col + 1
    } else if (action === 'DELETE_BLOCK') {
        newRow = row - 1
        newCol = col
    }

    return `hex-block-${newRow}-${newCol}`
}

// ──────────────────────────────────────────────
// Default logic state (matches defaultLogic in index.js)
// ──────────────────────────────────────────────

const createDefaultLogic = () => ({
    program: [
        {
            type: 'start',
            state: {
                bic1: false,
                bic2: false,
                bic3: false,
                bid1: false,
                bid2: false,
                bif1: false,
                bif2: false,
                bif3: false,
                bidata1: false,
                bidata2: false,
                asgn1: false,
                asgn2: false,
                asgn3: false,
                asgn4: false,
                asgn5: false,
            },
            bic1: false,
            bic2: false,
            bic3: false,
            bid1: false,
            bid2: false,
            bif1: false,
            bif2: false,
            bif3: false,
            bid3: false,
            bmp3: false,
            bidata1: false,
            bidata2: false,
            asgn1: false,
            asgn2: false,
            asgn3: false,
            asgn4: false,
            asgn5: false,
            id: '10',
            row: 1,
            col: 1,
        },
    ],
    end: { type: 'end', state: 'repeat' },
    insertState: false,
    offset: { left: 0, top: 90 },
    scale: 1.3,
    currentProgramGuide: 0,
    active: [-1, -1],
    bottomPanel: 'border',
    CUSTOMGROUPACTIONS12: {},
})

// ──────────────────────────────────────────────
// Action application (mutates logic in-place)
// ──────────────────────────────────────────────

const applyAction = (logic, actionDef, activeBlockRef) => {
    if (!actionDef || !actionDef.action) return

    switch (actionDef.action) {
        case 'PLACE_BLOCK': {
            const internalType =
                BLOCK_TYPE_MAP[actionDef.blockType] || actionDef.blockType

            // 1. Handle structural scope-closing blocks
            if (internalType.startsWith('end_')) {
                if (logic.currentProgramGuide > 0) {
                    logic.currentProgramGuide--
                }
                logic.active = [-1, -1]
                logic.bottomPanel = 'border'
                activeBlockRef.current = null
                activeBlockRef.currentIndex = -1
                break // Stop here, do not push to the logic array
            }

            const newId = generateBlockId()
            const gridPos = actionDef.gridPosition || {}

            const newNode = {
                type: internalType,
                state: {},
                id: newId,
                row: gridPos.row || 1,
                col: gridPos.col || 1,
            }

            if (TYPES_WITH_SUBPROGRAM.includes(internalType)) {
                newNode.subprogram = []
            }

            if (internalType === 'end') {
                logic.end = { type: 'end', state: 'end' }
            }

            // 2. 🚨 Navigate to the correct nested array using currentProgramGuide
            let targetArray = logic.program
            let currentDepth = logic.currentProgramGuide

            while (currentDepth > 0 && targetArray.length > 0) {
                const lastNode = targetArray[targetArray.length - 1]
                if (!lastNode.subprogram) {
                    lastNode.subprogram = []
                }
                targetArray = lastNode.subprogram
                currentDepth--
            }

            // 3. Push to the correct nested level, NOT the root array!
            targetArray.push(newNode)

            // 4. Increment the guide AFTER pushing if it's a container block
            // (This matches LogicTwoContent's real behavior)
            if (TYPES_WITH_SUBPROGRAM.includes(internalType)) {
                logic.currentProgramGuide++
            }

            logic.active = [-1, -1]
            logic.bottomPanel = 'border'

            activeBlockRef.current = newNode
            activeBlockRef.currentIndex = targetArray.length - 1
            break
        }

        case 'INSERT_BLOCK': {
            const internalType =
                BLOCK_TYPE_MAP[actionDef.blockType] || actionDef.blockType
            const newId = generateBlockId()
            const gridPos = actionDef.gridPosition || {}

            const newNode = {
                type: internalType,
                state: {},
                id: newId,
                row: gridPos.row || 1,
                col: gridPos.col || 1,
            }

            if (TYPES_WITH_SUBPROGRAM.includes(internalType)) {
                newNode.subprogram = []
            }

            if (internalType === 'end') {
                logic.end = { type: 'end', state: 'end' }
            }

            // Navigate to the correct parent array using nestingPath
            let parentArr = logic.program
            const nestingPath = actionDef.nestingPath || []
            for (let i = 0; i < nestingPath.length; i++) {
                const idx = nestingPath[i]
                if (parentArr[idx] && parentArr[idx].subprogram) {
                    parentArr = parentArr[idx].subprogram
                }
            }

            const insertIdx =
                actionDef.insertIndex != null
                    ? actionDef.insertIndex
                    : parentArr.length

            // Splice the new node at the insert position
            parentArr.splice(insertIdx, 0, newNode)

            logic.active = [-1, -1]
            logic.bottomPanel = 'border'

            activeBlockRef.current = newNode
            activeBlockRef.currentIndex = insertIdx
            break
        }

        case 'DELETE_BLOCK': {
            // Navigate to the correct parent array using nestingPath
            let parentArr = logic.program
            const nestingPath = actionDef.nestingPath || []
            for (let i = 0; i < nestingPath.length; i++) {
                const idx = nestingPath[i]
                if (parentArr[idx] && parentArr[idx].subprogram) {
                    parentArr = parentArr[idx].subprogram
                }
            }

            const deleteIdx =
                actionDef.deleteIndex != null ? actionDef.deleteIndex : -1

            if (deleteIdx >= 0 && deleteIdx < parentArr.length) {
                const deletedBlock = parentArr[deleteIdx]

                // Handle end/repeat block deletion
                if (
                    deletedBlock.type === 'end' ||
                    deletedBlock.type === 'repeat'
                ) {
                    logic.end = { type: 'end', state: 'repeat' }
                }

                // If deleting a nesting block at the end, adjust currentProgramGuide
                if (
                    deleteIdx === parentArr.length - 1 &&
                    TYPES_WITH_SUBPROGRAM.includes(deletedBlock.type)
                ) {
                    if (logic.currentProgramGuide > 0) {
                        logic.currentProgramGuide--
                    }
                }

                parentArr.splice(deleteIdx, 1)
            }

            logic.active = [-1, -1]
            logic.bottomPanel = 'border'
            activeBlockRef.current = null
            activeBlockRef.currentIndex = -1
            break
        }

        case 'OPEN_MENU': {
            const block = activeBlockRef.current
            if (block) {
                logic.active = [block.row || 1, block.col || 1]
                logic.bottomPanel = 'show'
            }
            break
        }

        case 'CLOSE_MENU': {
            logic.bottomPanel = 'border'
            logic.active = [-1, -1]
            break
        }

        case 'SET_VALUE':
        case 'TOGGLE_HARDWARE': {
            const block = activeBlockRef.current
            if (block && actionDef.hardwareKey) {
                if (typeof block.state !== 'object' || block.state === null) {
                    block.state = {}
                }
                block.state[actionDef.hardwareKey] = actionDef.expectedValue
            }
            break
        }

        case 'SET_STATE': {
            const block = activeBlockRef.current
            if (!block) break

            if (actionDef.hardwareKey === 'state') {
                if (block.type === 'end' || block.type === 'repeat') {
                    if (actionDef.expectedValue === 'repeat') {
                        block.type = 'repeat'
                        block.state = 'repeat'
                        logic.end = { type: 'end', state: 'repeat' }
                    } else {
                        block.type = 'end'
                        block.state = actionDef.expectedValue
                        logic.end = {
                            type: 'end',
                            state: actionDef.expectedValue,
                        }
                    }
                } else {
                    if (
                        typeof block.state !== 'object' ||
                        block.state === null
                    ) {
                        block.state = {}
                    }
                    block.state[actionDef.hardwareKey] = actionDef.expectedValue
                }
            } else if (actionDef.hardwareKey) {
                if (typeof block.state !== 'object' || block.state === null) {
                    block.state = {}
                }
                block.state[actionDef.hardwareKey] = actionDef.expectedValue
            }
            break
        }

        case 'SELECT_READ_SOURCE': {
            const block = activeBlockRef.current
            if (block) {
                if (typeof block.state !== 'object' || block.state === null) {
                    block.state = {}
                }
                block.state._readSource = actionDef.expectedValue
            }
            break
        }

        case 'READ_SENSOR': {
            const block = activeBlockRef.current
            if (block) {
                if (typeof block.state !== 'object' || block.state === null) {
                    block.state = {}
                }
                block.state._readAction = actionDef.expectedValue || true
            }
            break
        }

        default:
            console.warn(
                'TutorialReplayEngine: Unknown action type:',
                actionDef.action
            )
            break
    }
}

// ──────────────────────────────────────────────
// Highlight helpers
// ──────────────────────────────────────────────

/**
 * Determine what UI elements should be highlighted for a given action.
 * @param {Object} actionDef - A subStep action descriptor
 * @param {Object} parentStep - The parent codeStep (PLACE_BLOCK step)
 * @returns {{ highlightHex: string|null, panelHighlight: Object|null }}
 */
const getHighlightForAction = (actionDef, parentStep) => {
    if (!actionDef) return { highlightHex: null, panelHighlight: null }

    switch (actionDef.action) {
        case 'OPEN_MENU':
            return {
                highlightHex:
                    parentStep?.boardTarget || actionDef.uiTarget || null,
                panelHighlight: null,
            }

        case 'CLOSE_MENU':
            return {
                highlightHex: null,
                panelHighlight: {
                    changedKeys: [],
                    panelType: parentStep?.blockType || null,
                    uiTarget: actionDef.uiTarget || 'btn_modal_close',
                },
            }

        case 'TOGGLE_HARDWARE':
        case 'SET_VALUE':
        case 'SET_STATE':
            return {
                highlightHex: null,
                panelHighlight: {
                    changedKeys: actionDef.hardwareKey
                        ? [actionDef.hardwareKey]
                        : [],
                    panelType: parentStep?.blockType || null,
                    uiTarget: actionDef.uiTarget || null,
                    expectedValue:
                        actionDef.expectedValue !== undefined
                            ? actionDef.expectedValue
                            : null,
                },
            }

        case 'SELECT_READ_SOURCE':
            return {
                highlightHex: null,
                panelHighlight: {
                    changedKeys: ['readSource'],
                    panelType: parentStep?.blockType || 'sensor',
                    uiTarget: actionDef.uiTarget || 'readDropdown',
                    expectedValue:
                        actionDef.expectedValue !== undefined
                            ? actionDef.expectedValue
                            : null,
                },
            }

        case 'READ_SENSOR':
            return {
                highlightHex: null,
                panelHighlight: {
                    changedKeys: ['readAction'],
                    panelType: parentStep?.blockType || 'sensor',
                    uiTarget: actionDef.uiTarget || 'readButton',
                },
            }

        default:
            return { highlightHex: null, panelHighlight: null }
    }
}

/**
 * Generate micro-slides for a sequence of subSteps.
 * Expands SELECT_READ_SOURCE into 4 slides (highlight dropdown → select → highlight read → click read)
 * and READ_SENSOR into 2 slides (highlight Read button → click).
 */
const generateSubStepSlides = (subSteps, step, slides, codeStepIndex) => {
    const panelType = step.blockType || 'sensor'

    for (let j = 0; j < subSteps.length - 1; j++) {
        const nextSub = subSteps[j + 1]

        // ── SELECT_READ_SOURCE: 4 slides ──
        if (nextSub.action === 'SELECT_READ_SOURCE') {
            const readValue = nextSub.expectedValue || ''

            // i. Highlight the dropdown
            slides.push({
                codeStepIndex,
                phase: 'after_substep',
                subStepIndex: j,
                title: 'Click the Read Dropdown',
                instruction:
                    nextSub.instruction ||
                    'Click the Read dropdown to select a source.',
                highlightHex: null,
                panelHighlight: {
                    changedKeys: ['readDropdownHighlight'],
                    panelType,
                    uiTarget: 'readDropdown',
                    expectedValue: readValue,
                },
            })

            // ii. Choose the option (apply SELECT_READ_SOURCE)
            // slides.push({
            //     codeStepIndex,
            //     phase: 'after_substep',
            //     subStepIndex: j + 1,
            //     title: `"${readValue}" Selected`,
            //     instruction: `"${readValue}" has been selected.`,
            //     highlightHex: null,
            //     panelHighlight: {
            //         // changedKeys: ['readSource'],   // to highlight the whole thing
            //         changedKeys: ['readDropdownHighlight'],
            //         panelType,
            //         uiTarget: 'readDropdown',
            //         expectedValue: readValue
            //     },
            // })

            // iii. Highlight the Read Button
            slides.push({
                codeStepIndex,
                phase: 'after_substep',
                subStepIndex: j + 1, // Keep subStepIndex same to tie it to this block
                title: 'Click the Read Button',
                instruction: `Click the Read button to fetch the ${readValue} input value.`,
                highlightHex: null,
                panelHighlight: {
                    changedKeys: ['readButtonHighlight'],
                    panelType,
                    uiTarget: 'readButton',
                },
                // Override: subStepIndex+1 would point to CLOSE_MENU, but we need READ_SENSOR
                overrideExpectedAction: {
                    type: 'READ_SENSOR',
                    hardwareKey: '_readAction',
                },
            })

            // iv. Click the Read Button (Auto-simulated READ_SENSOR)
            // slides.push({
            //     codeStepIndex,
            //     phase: 'after_substep',
            //     subStepIndex: j + 1,
            //     title: 'Reading Input Values',
            //     instruction: 'The input value has been read.',
            //     highlightHex: null,
            //     panelHighlight: {
            //         changedKeys: ['readAction'],
            //         panelType,
            //         uiTarget: 'readButtonClick',
            //     },
            // })
            continue
        }

        // ── READ_SENSOR: 2 slides (Kept as fallback in case capture DOES fire it) ──
        if (nextSub.action === 'READ_SENSOR') {
            slides.push({
                codeStepIndex,
                phase: 'after_substep',
                subStepIndex: j,
                title: 'Click the Read Button',
                instruction: nextSub.instruction || 'Click the Read button.',
                highlightHex: null,
                panelHighlight: {
                    changedKeys: ['readButtonHighlight'],
                    panelType,
                    uiTarget: 'readButton',
                },
            })

            slides.push({
                codeStepIndex,
                phase: 'after_substep',
                subStepIndex: j + 1,
                title: 'Reading Input Values',
                instruction: 'The input value has been read.',
                highlightHex: null,
                panelHighlight: {
                    changedKeys: ['readAction'],
                    panelType,
                    uiTarget: 'readButtonClick',
                },
            })
            continue
        }

        // ── Normal subStep: 1 slide ──
        const nextHighlight = getHighlightForAction(nextSub, step)
        let slideTitle = nextSub.instruction || ''
        if (slideTitle.length > 50) {
            slideTitle = slideTitle.substring(0, 47) + '...'
        }
        slides.push({
            codeStepIndex,
            phase: 'after_substep',
            subStepIndex: j,
            title: slideTitle,
            instruction: nextSub.instruction,
            highlightHex: nextHighlight.highlightHex,
            panelHighlight: nextHighlight.panelHighlight,
        })
    }
}

// ──────────────────────────────────────────────
// flattenToMicroSlides  (main export)
// ──────────────────────────────────────────────

/**
 * Expand codeSteps into a flat array of micro-slides.
 *
 * For each PLACE_BLOCK codeStep with N subSteps, we generate:
 *   1. pre_select      — board before this step, highlight insert point
 *   2. select_active   — insert point active, choose block type
 *   3. block_placed    — block on board, message = subSteps[0].instruction
 *   4..N+1. after_substep[0..N-2] — state after subStep[j], message = subSteps[j+1].message
 *
 * Total per codeStep = 2 + 1 + (N-1) = N + 2  (where N = subSteps.length)
 * Plus 1 final "complete" slide.
 *
 * For talking-dragon: 6+5+6+5+5+1 = 28 slides (matches old v1.0 format).
 *
 * @param {Object} tutorialData - The full v2.0 tutorial data
 * @returns {Array} Array of micro-slide descriptor objects
 */
export const flattenToMicroSlides = (tutorialData) => {
    const codeSteps = tutorialData?.codeSteps || tutorialData?.steps || []
    if (codeSteps.length === 0) return []

    const slides = []

    for (let i = 0; i < codeSteps.length; i++) {
        const step = codeSteps[i]
        const subSteps = step.subSteps || []
        const blockLabel = BLOCK_LABELS[step.blockType] || step.blockType
        const action = step.action || 'PLACE_BLOCK'

        console.log('Step = ', step)

        // ── INSERT_BLOCK ─────────────────────────────
        if (action === 'INSERT_BLOCK') {
            // Phase 1: Click the existing block
            slides.push({
                codeStepIndex: i,
                phase: 'pre_insert',
                title: 'Click the Block',
                instruction:
                    step.preMessage ||
                    `First, click the ${blockLabel} block where you want to insert.`,
                highlightHex: step.boardTarget, // Highlight the block itself
                panelHighlight: null,
            })

            // Phase 1.5: Click Insert button — shows block options
            slides.push({
                codeStepIndex: i,
                phase: 'insert_menu_open',
                title: 'Click Insert Button',
                instruction:
                    step.selectMessage ||
                    'Click the Insert (+) button above the block.',
                highlightHex: getExistingBlockOptions(
                    step.boardTarget,
                    step.action
                ), // Highlight the mini button
                panelHighlight: null,
            })

            // Phase 2: Choose block type
            slides.push({
                codeStepIndex: i,
                phase: 'insert_choose',
                title: `Choose ${blockLabel}`,
                instruction: `Choose the ${blockLabel} block.`,
                highlightHex: getNewBlockPosition(
                    step.boardTarget,
                    step.blockType
                ),
                panelHighlight: null,
            })

            // Phase 3+: Block inserted, then iterate subSteps if any
            if (subSteps.length > 0) {
                const firstSubHighlight = getHighlightForAction(
                    subSteps[0],
                    step
                )
                slides.push({
                    codeStepIndex: i,
                    phase: 'block_inserted',
                    title:
                        subSteps[0]?.action === 'OPEN_MENU'
                            ? `Double-Click ${blockLabel} Block`
                            : `${blockLabel} Block Inserted`,
                    instruction: subSteps[0].instruction,
                    highlightHex:
                        firstSubHighlight.highlightHex || step.boardTarget,
                    panelHighlight: firstSubHighlight.panelHighlight,
                })

                generateSubStepSlides(subSteps, step, slides, i)
            } else {
                slides.push({
                    codeStepIndex: i,
                    phase: 'block_inserted',
                    title: `${blockLabel} Block Inserted`,
                    instruction:
                        step.instruction ||
                        `The ${blockLabel} block has been inserted.`,
                    highlightHex: step.boardTarget,
                    panelHighlight: null,
                })
            }
            continue
        }

        // ── DELETE_BLOCK ────────────────────────────
        if (action === 'DELETE_BLOCK') {
            // Phase 1: Click the block
            slides.push({
                codeStepIndex: i,
                phase: 'pre_delete',
                title: `Click the ${blockLabel} Block`,
                instruction:
                    step.preMessage ||
                    step.instruction ||
                    `Click the ${blockLabel} block.`,
                highlightHex: step.boardTarget, // Highlight the block itself
                panelHighlight: null,
            })

            // Phase 1.5: Click the Delete button
            slides.push({
                codeStepIndex: i,
                phase: 'delete_menu_open',
                title: 'Click Delete Button',
                instruction:
                    step.selectMessage ||
                    'Click the Delete button above the block.',
                highlightHex: getExistingBlockOptions(
                    step.boardTarget,
                    step.action
                ), // Highlight the mini button
                panelHighlight: null,
            })

            // Phase 2: Block is deleted
            slides.push({
                codeStepIndex: i,
                phase: 'block_deleted',
                title: `${blockLabel} Block Deleted`,
                instruction:
                    step.instruction || `The ${blockLabel} block has been deleted.`,
                highlightHex: null,
                panelHighlight: null,
            })
            continue
        }

        // ── PLACE_BLOCK (default) ────────────────────

        // ── Phase 1: Pre-select ──────────────────────
        // Board shows all previous steps fully applied. Insert point visible.
        slides.push({
            codeStepIndex: i,
            phase: 'pre_select',
            title: i === 0 ? 'Click Select Block' : 'Click Select Block',
            instruction:
                step.preMessage ||
                (i === 0
                    ? 'First, click the Select block.'
                    : 'Click the Select block again.'),
            highlightHex: step.boardTarget,
            panelHighlight: null,
        })

        // ── Phase 2: Select active ───────────────────
        // Insert point is now active (clicked). User must choose a block type.
        slides.push({
            codeStepIndex: i,
            phase: 'select_active',
            title: `Choose ${blockLabel} Block`,
            instruction:
                step.selectMessage || `Choose the ${blockLabel} block.`,
            highlightHex: getNewBlockPosition(step.boardTarget, step.blockType),
            panelHighlight: null,
        })

        if (subSteps.length > 0) {
            // ── Phase 3: Block placed ────────────────
            // Block is on the board. Message = first subStep (usually OPEN_MENU).
            const firstSubHighlight = getHighlightForAction(subSteps[0], step)
            slides.push({
                codeStepIndex: i,
                phase: 'block_placed',
                title:
                    subSteps[0]?.action === 'OPEN_MENU'
                        ? `Double-Click ${blockLabel} Block`
                        : `${blockLabel} Block Placed`,
                instruction: subSteps[0].instruction,
                highlightHex:
                    firstSubHighlight.highlightHex || step.boardTarget,
                panelHighlight: firstSubHighlight.panelHighlight,
            })

            // ── Phase 4+: After each subStep (except the last) ──
            // State after subSteps[0..j], message = subSteps[j+1].message
            generateSubStepSlides(subSteps, step, slides, i)
        } else {
            // No subSteps — just the block placed
            slides.push({
                codeStepIndex: i,
                phase: 'block_placed',
                title: `${blockLabel} Block Placed`,
                instruction:
                    step.instruction || `The ${blockLabel} block has been placed.`,
                highlightHex: step.boardTarget,
                panelHighlight: null,
            })
        }
    }

    // ── Final slide: Tutorial Complete ────────────
    slides.push({
        codeStepIndex: codeSteps.length,
        phase: 'complete',
        title: tutorialData?.finalStepTitle || 'Tutorial Complete!',
        instruction: tutorialData?.finalStepInstruction || 'Excellent! You have successfully built the logic.',
        highlightHex: null,
        panelHighlight: null,
    })

    return slides
}

// ──────────────────────────────────────────────
// buildStateForMicroSlide  (main export)
// ──────────────────────────────────────────────

/**
 * Build the complete hex board state for a given micro-slide.
 *
 * @param {Array} codeSteps - The codeSteps array from the v2.0 JSON
 * @param {Object} microSlide - A micro-slide descriptor from flattenToMicroSlides()
 * @returns {{ logic: Object, currentActiveHexBlock: Object|null }}
 */
export const buildStateForMicroSlide = (codeSteps, microSlide) => {
    resetBlockIdCounter()

    const logic = createDefaultLogic()
    const activeBlockRef = { current: null, currentIndex: -1 }

    if (!microSlide) return { logic, currentActiveHexBlock: null }

    const { codeStepIndex, phase, subStepIndex } = microSlide

    // 1. Fully apply ALL codeSteps before the current one
    const stepsToFullyApply = Math.min(codeStepIndex, codeSteps.length)
    for (let i = 0; i < stepsToFullyApply; i++) {
        applyAction(logic, codeSteps[i], activeBlockRef)
        if (codeSteps[i].subSteps) {
            for (const sub of codeSteps[i].subSteps) {
                applyAction(logic, sub, activeBlockRef)
            }
        }
    }

    // 2. Handle the current codeStep based on the micro-slide phase
    if (codeStepIndex < codeSteps.length) {
        const step = codeSteps[codeStepIndex]

        switch (phase) {
            case 'pre_select':
                // Board after all previous steps, nothing active
                logic.active = [-1, -1]
                logic.bottomPanel = 'border'
                console.log('Pre select slide - logic state:', logic)
                break

            case 'select_active': {
                // Same board, but insert point is now active
                const gridPos = step.gridPosition || {}
                logic.active = [
                    gridPos.row || 1,
                    gridPos.col || logic.program.length + 1,
                ]
                logic.bottomPanel = 'border'
                console.log('Select active slide - logic state:', logic)
                break
            }

            case 'block_placed':
                // Apply PLACE_BLOCK only — block on board, no menu
                applyAction(logic, step, activeBlockRef)
                // When the first sub-step is CLOSE_MENU the panel must be open
                // so the user can immediately close it. Set active to the placed
                // block and show the panel; otherwise keep the board in the
                // neutral (no selection, panel closed) state.
                if (step.subSteps?.[0]?.action === 'CLOSE_MENU') {
                    const placed = activeBlockRef.current
                    logic.active = placed
                        ? [placed.row, placed.col]
                        : [-1, -1]
                    logic.bottomPanel = 'show'
                } else {
                    logic.active = [-1, -1]
                    logic.bottomPanel = 'border'
                }
                break

            case 'after_substep': {
                // Apply PLACE_BLOCK + subSteps[0..subStepIndex]
                applyAction(logic, step, activeBlockRef)
                if (step.subSteps) {
                    const maxSub = Math.min(
                        (subStepIndex ?? -1) + 1,
                        step.subSteps.length
                    )
                    for (let k = 0; k < maxSub; k++) {
                        applyAction(logic, step.subSteps[k], activeBlockRef)
                    }
                }
                break
            }

            // ── INSERT_BLOCK phases ──────────────────
            case 'pre_insert': {
                // Block NOT clicked yet
                logic.active = [-1, -1]
                logic.insertState = false
                logic.bottomPanel = 'border'
                break
            }

            case 'insert_menu_open': {
                // Block is clicked → shows delete + insert buttons above it
                const preInsGridPos = step.gridPosition || {}
                logic.active = [preInsGridPos.row || 1, preInsGridPos.col || 1]
                logic.insertState = false
                logic.bottomPanel = 'border'
                break
            }

            case 'insert_choose': {
                // User clicked Insert → shows 4 block-type options
                const insChooseGridPos = step.gridPosition || {}
                logic.active = [
                    insChooseGridPos.row || 1,
                    insChooseGridPos.col || 1,
                ]
                logic.insertState = true
                logic.bottomPanel = 'border'
                break
            }

            case 'block_inserted': {
                applyAction(logic, step, activeBlockRef)
                logic.active = [-1, -1]
                logic.insertState = false
                logic.bottomPanel = 'border'
                break
            }

            // ── DELETE_BLOCK phases ──────────────────
            case 'pre_delete': {
                // Block NOT clicked yet
                logic.active = [-1, -1]
                logic.insertState = false
                logic.bottomPanel = 'border'
                break
            }

            case 'delete_menu_open': {
                // Block is clicked → shows delete + insert buttons above it
                const preDelGridPos = step.gridPosition || {}
                logic.active = [preDelGridPos.row || 1, preDelGridPos.col || 1]
                logic.insertState = false
                logic.bottomPanel = 'border'
                break
            }

            case 'block_deleted': {
                applyAction(logic, step, activeBlockRef)
                logic.active = [-1, -1]
                logic.bottomPanel = 'border'
                break
            }

            case 'complete':
                // All steps already applied above
                logic.active = [-1, -1]
                logic.bottomPanel = 'border'
                break

            default:
                break
        }
    } else {
        // Beyond all codeSteps (complete slide)
        logic.active = [-1, -1]
        logic.bottomPanel = 'border'
    }

    // Build currentActiveHexBlock if panel is open
    let currentActiveHexBlock = null
    if (activeBlockRef.current && logic.bottomPanel === 'show') {
        currentActiveHexBlock = {
            type: activeBlockRef.current.type,
            state:
                typeof activeBlockRef.current.state === 'object'
                    ? { ...activeBlockRef.current.state }
                    : activeBlockRef.current.state,
            id: activeBlockRef.current.id,
        }
    }

    return { logic, currentActiveHexBlock }
}